import { AutocompleteInputChangeReason, Box } from '@mui/material';
import {
  FunctionComponent,
  useState,
  useEffect,
  useContext,
  createContext,
  useRef,
  forwardRef,
  ForwardedRef,
  PropsWithChildren,
  HTMLAttributes,
  cloneElement,
  ReactElement,
} from 'react';
import { FixedSizeList, ListChildComponentProps } from 'react-window';

import Autocomplete, { AutocompleteOptions } from '@Components/Autocomplete';

import {
  AutocompleteContextType,
  VirtualizedAutocompleteProps,
} from './VirtualizedAutocomplete.types';

/** Context to be passed to Virtualized List Adapter */
const AutocompleteContext = createContext<AutocompleteContextType>({
  options: [],
  selectedOptions: [],
  reason: '',
});

const LISTBOX_PADDING = 8; // px

/** Virtualized List Row */
const renderRow = (rowsProps: ListChildComponentProps<ReactElement[]>) => {
  const { data, index, style } = rowsProps;
  const dataSet = data[index];
  const inlineStyle = {
    ...style,
    top: (style.top as number) + LISTBOX_PADDING,
  };

  return cloneElement(dataSet, {
    style: {
      ...inlineStyle,
    },
  });
};

/** Virtualized List ListBox */
const OuterElementContext = createContext({});
const OuterElementType = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(
  (
    outerElementProps: PropsWithChildren<HTMLAttributes<HTMLDivElement>>,
    ref: ForwardedRef<HTMLDivElement>
  ) => {
    const outerProps = useContext(OuterElementContext);
    return <Box ref={ref} {...outerElementProps} {...outerProps} />;
  }
);
OuterElementType.displayName = 'OuterElementType';

/** Adapter for react-window */
const ListboxComponent = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLElement>
>(
  (
    listBoxProps: PropsWithChildren<HTMLAttributes<HTMLElement>>,
    ref: ForwardedRef<HTMLDivElement>
  ) => {
    const { children, ...other } = listBoxProps;

    const { options, selectedOptions, reason } =
      useContext(AutocompleteContext);

    const itemData: ReactElement[] = [];
    (children as ReactElement[]).forEach(
      (
        item: ReactElement & {
          children?: ReactElement[];
        }
      ) => {
        itemData.push(item);
        itemData.push(...(item.children || []));
      }
    );

    const itemCount = itemData.length;
    const itemSize = 40;

    const getChildSize = () => itemSize;

    const getHeight = () => {
      if (itemCount > 8) {
        return 8 * itemSize;
      }

      return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
    };

    const gridRef = useRef<FixedSizeList>(null);

    /** This handles the scroll of list according to the chosen option and when opened */
    useEffect(() => {
      if (gridRef.current && selectedOptions.length && reason === 'reset') {
        const index = options.findIndex(
          (item) =>
            item.value === selectedOptions[selectedOptions.length - 1].value
        );
        gridRef.current.scrollToItem(index, 'smart');
      }

      if (gridRef.current && selectedOptions.length && reason !== 'reset') {
        const index = options.findIndex(
          (item) => item.value === selectedOptions[0].value
        );
        gridRef.current.scrollToItem(index, 'smart');
      }
    }, [gridRef, reason, options, selectedOptions]);

    const outerRef = useRef<HTMLDivElement>(null);

    return (
      <Box ref={ref}>
        <OuterElementContext.Provider value={other}>
          <FixedSizeList
            itemData={itemData}
            height={getHeight() + 2 * LISTBOX_PADDING}
            width="100%"
            ref={gridRef}
            outerRef={outerRef}
            outerElementType={OuterElementType}
            innerElementType="ul"
            itemSize={getChildSize()}
            overscanCount={5}
            itemCount={itemCount}
          >
            {renderRow}
          </FixedSizeList>
        </OuterElementContext.Provider>
      </Box>
    );
  }
);
ListboxComponent.displayName = 'ListboxComponent';
/** <-- VIRTUALIZED LIST */

const VirtualizedAutocomplete: FunctionComponent<VirtualizedAutocompleteProps> =
  (props: VirtualizedAutocompleteProps): JSX.Element => {
    const { options, value, onInputChange, ...resProps } = props;

    const [reasonState, setReasonState] = useState<string>('');

    /** Handles Input Change */
    const onInputChangeHandler = (
      event: React.SyntheticEvent<Element, Event>,
      inputChangeValue: string,
      reason: AutocompleteInputChangeReason
    ) => {
      if (onInputChange) {
        onInputChange(event, inputChangeValue, reason);
      }
      setReasonState(reason);
    };

    return (
      <AutocompleteContext.Provider
        value={{
          options,
          selectedOptions: value as AutocompleteOptions[],
          reason: reasonState,
        }}
      >
        <Autocomplete
          options={options}
          value={value}
          ListboxComponent={ListboxComponent}
          onInputChange={onInputChangeHandler}
          disableListWrap
          {...resProps}
        />
      </AutocompleteContext.Provider>
    );
  };

export default VirtualizedAutocomplete;
